import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Mail,
    User,
    Calendar,
    CheckCircle2,
    Clock,
    Trash2,
    X,
    Search,
    Loader2,
    MessageSquare,
    ExternalLink,
    Filter,
    AlertCircle,
    MoreHorizontal
} from 'lucide-react';
import api from '../../services/api';
import toast from 'react-hot-toast';

const QueryManager = () => {
    const [queries, setQueries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedQuery, setSelectedQuery] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [filterStatus, setFilterStatus] = useState('All');

    const navigate = useNavigate();

    useEffect(() => {
        fetchQueries();
    }, []);

    // Refetch when search term changes with debounce
    useEffect(() => {
        const timer = setTimeout(() => {
            fetchQueries();
        }, 500);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    const fetchQueries = async () => {
        try {
            setLoading(true);
            const res = await api.get(`/queries?search=${searchTerm}`);
            if (res.data.success) {
                setQueries(res.data.data);
            }
        } catch (error) {
            console.error('Error fetching queries:', error);
            if (error.response?.status === 401) {
                toast.error('Session expired. Please login again.');
                navigate('/admin/login');
            } else {
                toast.error('Failed to load inquiries');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateStatus = async (id, status) => {
        try {
            const res = await api.put(`/queries/${id}`, { status });
            if (res.data.success) {
                toast.success(`Status updated to ${status}`);
                setQueries(queries.map(q => q._id === id ? { ...q, status } : q));
                if (selectedQuery && selectedQuery._id === id) {
                    setSelectedQuery({ ...selectedQuery, status });
                }
            }
        } catch (error) {
            toast.error('Failed to update status');
        }
    };

    const handleDeleteQuery = async (id) => {
        if (!window.confirm('Are you sure you want to delete this inquiry?')) return;
        try {
            const res = await api.delete(`/queries/${id}`);
            if (res.data.success) {
                toast.success('Inquiry deleted successfully');
                setQueries(queries.filter(q => q._id !== id));
                if (isModalOpen) setIsModalOpen(false);
            }
        } catch (error) {
            toast.error('Failed to delete inquiry');
        }
    };

    const openQuery = (query) => {
        setSelectedQuery(query);
        setIsModalOpen(true);
    };

    const filteredQueries = queries.filter(q => {
        const matchesSearch = q.fullName.toLowerCase().includes(searchTerm.toLowerCase()) || 
                             q.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                             q.message.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesFilter = filterStatus === 'All' || q.status === filterStatus;
        return matchesSearch && matchesFilter;
    });

    const stats = {
        total: queries.length,
        new: queries.filter(q => q.status === 'New').length,
        resolved: queries.filter(q => q.status === 'Resolved').length
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'New': return 'bg-blue-500/10 text-blue-400 border-blue-500/20';
            case 'In Progress': return 'bg-amber-500/10 text-amber-400 border-amber-500/20';
            case 'Resolved': return 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20';
            case 'Spam': return 'bg-rose-500/10 text-rose-400 border-rose-500/20';
            default: return 'bg-slate-500/10 text-slate-400 border-slate-500/20';
        }
    };

    return (
        <div className="space-y-6 animate-in fade-in duration-500 pb-10">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-slate-800 pb-6">
                <div>
                    <h1 className="text-3xl font-bold text-white tracking-tight">Client Inquiries</h1>
                    <p className="text-slate-400 mt-1 text-sm">Manage and respond to project leads</p>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                    <div className="relative group min-w-[240px]">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-primary transition-colors" size={18} />
                        <input
                            type="text"
                            placeholder="Search inquiries..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full bg-slate-900 border border-slate-700 rounded-xl pl-10 pr-4 py-2.5 text-sm font-medium text-white placeholder:text-slate-500 focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/50 transition-all"
                        />
                    </div>
                    <div className="flex items-center bg-slate-900 border border-slate-700 rounded-xl px-2 py-1">
                        <Filter size={16} className="text-slate-500 ml-2" />
                        <select 
                            value={filterStatus}
                            onChange={(e) => setFilterStatus(e.target.value)}
                            className="bg-transparent border-none text-slate-300 text-sm focus:ring-0 cursor-pointer px-3 py-1.5"
                        >
                            <option value="All">All Status</option>
                            <option value="New">New</option>
                            <option value="In Progress">In Progress</option>
                            <option value="Resolved">Resolved</option>
                            <option value="Spam">Spam</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* Stats Overview */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-slate-900 border border-slate-800 p-6 rounded-2xl relative overflow-hidden group">
                    <div className="absolute -right-4 -top-4 text-primary/10 rotate-12 group-hover:rotate-6 transition-transform">
                        <MessageSquare size={120} />
                    </div>
                    <div className="relative z-10">
                        <p className="text-slate-400 text-xs font-bold uppercase tracking-widest mb-1">Total Inquiries</p>
                        <h3 className="text-3xl font-black text-white">{stats.total}</h3>
                        <p className="text-slate-500 text-xs mt-4 flex items-center gap-1.5">
                            <Clock size={12} /> lifetime total submissions
                        </p>
                    </div>
                </div>

                <div className="bg-slate-900 border border-slate-800 p-6 rounded-2xl relative overflow-hidden group">
                    <div className="absolute -right-4 -top-4 text-blue-500/10 rotate-12 group-hover:rotate-6 transition-transform">
                        <AlertCircle size={120} />
                    </div>
                    <div className="relative z-10">
                        <p className="text-slate-400 text-xs font-bold uppercase tracking-widest mb-1">Unresolved</p>
                        <h3 className="text-3xl font-black text-blue-400">{stats.new}</h3>
                        <p className="text-blue-500/60 text-xs mt-4 flex items-center gap-1.5 font-bold">
                            Requires Attention
                        </p>
                    </div>
                </div>

                <div className="bg-slate-900 border border-slate-800 p-6 rounded-2xl relative overflow-hidden group">
                    <div className="absolute -right-4 -top-4 text-emerald-500/10 rotate-12 group-hover:rotate-6 transition-transform">
                        <CheckCircle2 size={120} />
                    </div>
                    <div className="relative z-10">
                        <p className="text-slate-400 text-xs font-bold uppercase tracking-widest mb-1">Successfully Resolved</p>
                        <h3 className="text-3xl font-black text-emerald-400">{stats.resolved}</h3>
                        <p className="text-emerald-500/60 text-xs mt-4 flex items-center gap-1.5 font-bold">
                            Goal Achieved
                        </p>
                    </div>
                </div>
            </div>

            {/* Queries Table */}
            <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden shadow-xl">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="border-b border-slate-800 bg-slate-900/50">
                                <th className="px-6 py-4 text-xs font-black uppercase tracking-widest text-slate-500">Sender</th>
                                <th className="px-6 py-4 text-xs font-black uppercase tracking-widest text-slate-500 hidden lg:table-cell">Interest</th>
                                <th className="px-6 py-4 text-xs font-black uppercase tracking-widest text-slate-500 text-center">Status</th>
                                <th className="px-6 py-4 text-xs font-black uppercase tracking-widest text-slate-500 text-center">Date</th>
                                <th className="px-6 py-4 text-right text-xs font-black uppercase tracking-widest text-slate-500">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-800">
                            {loading ? (
                                <tr>
                                    <td colSpan="5" className="py-20 text-center">
                                        <Loader2 className="animate-spin text-primary mx-auto mb-4" size={32} />
                                        <p className="text-sm font-medium text-slate-500">Accessing secure inquiry database...</p>
                                    </td>
                                </tr>
                            ) : filteredQueries.length > 0 ? filteredQueries.map((query, idx) => (
                                <motion.tr
                                    key={query._id}
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    transition={{ delay: idx * 0.02 }}
                                    className="group hover:bg-slate-800/50 transition-all duration-300"
                                >
                                    <td className="px-6 py-5">
                                        <div className="flex items-center gap-4">
                                            <div className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center text-primary font-black shadow-inner border border-slate-700">
                                                {query.fullName.charAt(0).toUpperCase()}
                                            </div>
                                            <div>
                                                <h3 className="text-sm font-bold text-white group-hover:text-primary transition-colors">{query.fullName}</h3>
                                                <p className="text-[10px] text-slate-500 font-medium flex items-center gap-1 mt-0.5">
                                                    <Mail size={10} /> {query.email}
                                                </p>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-5 hidden lg:table-cell">
                                        <div className="inline-flex items-center px-2.5 py-1 rounded-lg bg-slate-800 text-[10px] font-black text-slate-300 border border-slate-700 uppercase tracking-tighter">
                                            {query.projectInterest}
                                        </div>
                                    </td>
                                    <td className="px-6 py-5 text-center">
                                        <div className="flex justify-center">
                                            <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-black border uppercase tracking-wider ${getStatusColor(query.status)}`}>
                                                <span className={`w-1.5 h-1.5 rounded-full ${query.status === 'New' ? 'bg-current animate-pulse' : 'bg-current'}`}></span>
                                                {query.status}
                                            </span>
                                        </div>
                                    </td>
                                    <td className="px-6 py-5 text-center">
                                        <div className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
                                            {new Date(query.createdAt).toLocaleDateString()}
                                        </div>
                                    </td>
                                    <td className="px-6 py-5 text-right">
                                        <div className="flex items-center justify-end gap-2">
                                            <button
                                                onClick={() => openQuery(query)}
                                                className="p-2 text-slate-500 hover:text-white hover:bg-slate-800 rounded-lg transition-all"
                                                title="View Message"
                                            >
                                                <ExternalLink size={16} />
                                            </button>
                                            <button
                                                onClick={() => handleDeleteQuery(query._id)}
                                                className="p-2 text-slate-500 hover:text-rose-400 hover:bg-rose-500/10 rounded-lg transition-all"
                                                title="Delete Inquiry"
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </div>
                                    </td>
                                </motion.tr>
                            )) : (
                                <tr>
                                    <td colSpan="5" className="py-20 text-center">
                                        <div className="w-16 h-16 bg-slate-800/50 rounded-full flex items-center justify-center mx-auto mb-4">
                                            <MessageSquare className="text-slate-600" size={32} />
                                        </div>
                                        <h3 className="text-white font-bold">Inquiry Cache Empty</h3>
                                        <p className="text-slate-500 text-sm mt-1">No project leads found matching criteria</p>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* View Query Modal */}
            <AnimatePresence>
                {isModalOpen && selectedQuery && (
                    <div className="fixed inset-0 z-[100] flex items-center justify-center p-6">
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setIsModalOpen(false)}
                            className="absolute inset-0 bg-slate-950/90 backdrop-blur-md"
                        />
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95, y: 20 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.95, y: 20 }}
                            className="bg-slate-900 border border-slate-800 w-full max-w-2xl rounded-3xl shadow-2xl overflow-hidden relative z-10"
                        >
                            {/* Modal Header */}
                            <div className="p-8 border-b border-slate-800 flex items-center justify-between bg-slate-900/50">
                                <div className="flex items-center gap-4">
                                    <div className="w-12 h-12 rounded-2xl bg-primary/10 text-primary flex items-center justify-center">
                                        <User size={24} />
                                    </div>
                                    <div>
                                        <h2 className="text-xl font-black text-white uppercase tracking-tight">{selectedQuery.fullName}</h2>
                                        <p className="text-xs text-slate-500 font-bold uppercase tracking-widest">{selectedQuery.email}</p>
                                    </div>
                                </div>
                                <button 
                                    onClick={() => setIsModalOpen(false)} 
                                    className="p-2 text-slate-500 hover:text-white hover:bg-slate-800 rounded-xl transition-colors"
                                >
                                    <X size={24} />
                                </button>
                            </div>

                            {/* Modal Body */}
                            <div className="p-8 space-y-8">
                                <div className="grid grid-cols-2 gap-6">
                                    <div className="space-y-1.5">
                                        <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest flex items-center gap-1.5">
                                            <Calendar size={12} /> Date Received
                                        </span>
                                        <p className="text-sm font-bold text-white uppercase">
                                            {new Date(selectedQuery.createdAt).toLocaleDateString('en-US', { 
                                                year: 'numeric', 
                                                month: 'long', 
                                                day: 'numeric',
                                                hour: '2-digit',
                                                minute: '2-digit'
                                            })}
                                        </p>
                                    </div>
                                    <div className="space-y-1.5">
                                        <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest flex items-center gap-1.5">
                                            <Filter size={12} /> Project Category
                                        </span>
                                        <p className="text-sm font-bold text-primary uppercase">
                                            {selectedQuery.projectInterest}
                                        </p>
                                    </div>
                                </div>

                                <div className="space-y-3">
                                    <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest block">Message Payload</span>
                                    <div className="bg-slate-950 border border-slate-800 p-6 rounded-2xl">
                                        <p className="text-slate-300 text-sm leading-relaxed whitespace-pre-wrap italic">
                                            "{selectedQuery.message}"
                                        </p>
                                    </div>
                                </div>

                                <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 pt-4 border-t border-slate-800">
                                    <div className="space-y-3">
                                        <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest block">Workflow Management</span>
                                        <div className="flex flex-wrap gap-2">
                                            {['New', 'In Progress', 'Resolved', 'Spam'].map((s) => (
                                                <button
                                                    key={s}
                                                    onClick={() => handleUpdateStatus(selectedQuery._id, s)}
                                                    className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest border transition-all ${selectedQuery.status === s
                                                        ? getStatusColor(s) + ' ring-2 ring-primary/20 scale-105 shadow-lg'
                                                        : 'bg-slate-800 border-slate-700 text-slate-500 hover:text-slate-300 hover:border-slate-600'
                                                    }`}
                                                >
                                                    {s}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="flex gap-3">
                                        <a 
                                            href={`mailto:${selectedQuery.email}`}
                                            className="px-6 py-3 bg-primary text-white text-xs font-black uppercase tracking-widest rounded-xl hover:bg-primary-dark transition-all flex items-center gap-2 shadow-xl shadow-primary/20"
                                        >
                                            <Mail size={16} /> Reply via Email
                                        </a>
                                        <button 
                                            onClick={() => handleDeleteQuery(selectedQuery._id)}
                                            className="p-3 bg-rose-500/10 text-rose-500 rounded-xl hover:bg-rose-500 hover:text-white transition-all border border-rose-500/20"
                                        >
                                            <Trash2 size={20} />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default QueryManager;
